import React, { useEffect, useState } from 'react';
import { useToast } from '@contexts/Toast';
import { errorMessage, responseTexts } from '@helpers/Constants';
import { AddRoleModalProps, Role } from '@helpers/Models';
import { Axios } from '@helpers/ApiService';
import { ApiEndpoints } from '@helpers/Constants/Navigation';
import { AxiosError } from 'axios';

export const AddUserModal: React.FC<AddRoleModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { showToastMessage } = useToast();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [roles, setRoles] = useState<Role[]>([]);
  const [rolePermissions, setRolePermissions] = useState<number[]>([]);
  const [emailError, setEmailError] = useState('');
  const [nameError, setNameError] = useState('');
  const [duplicateError, setDuplicateError] = useState('');

  // Reset all form state
  const resetForm = () => {
    setName('');
    setEmail('');
    setRolePermissions([]);
    setEmailError('');
    setNameError('');
    setDuplicateError('');
  };

  // Fetch roles when modal opens
  useEffect(() => {
    if (isOpen) {
      // Clear any stale errors before fetching
      resetForm();
      fetchRoles();
    } else {
      // Reset when modal closes (Cancel or backdrop click)
      resetForm();
    }
  }, [isOpen]);

  const fetchRoles = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_ROLES, {});
      setRoles(response.data.data);
    } catch (error) {
      showToastMessage(errorMessage.ERROR_FETCHING_MODULES, 'error');
    }
  };

  const handleCheckboxChange = (roleId: number) => {
    return (event: React.ChangeEvent<HTMLInputElement>): void => {
      if (event.target.checked) {
        setRolePermissions([...rolePermissions, roleId]);
      } else {
        setRolePermissions(rolePermissions.filter((id) => id !== roleId));
      }
    };
  };

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();

    let isValid = true;

    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address.');
      isValid = false;
    } else {
      setEmailError('');
    }

    if (!name.trim()) {
      setNameError('Please enter a valid name.');
      isValid = false;
    } else {
      setNameError('');
    }

    if (!isValid) return;

    try {
      const payload = {
        name: name.trim(),
        email: email.trim(),
        roleId: rolePermissions,
      };

      await Axios.post(ApiEndpoints.UPSERT_USER, payload);
      // Clear duplicate error if any
      setDuplicateError('');
      // Close and reset (resetForm will be called by the isOpen effect when onClose sets isOpen to false)
      onClose();
      showToastMessage(responseTexts.USER_CREATION_SUCCESS, 'success');
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      if (
        axiosError.response?.data?.message === errorMessage.USER_ALREADY_EXISTS
      ) {
        setDuplicateError(axiosError.response.data.message);
      } else {
        showToastMessage(errorMessage.ERROR_CREATING_USER, 'error');
      }
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^[a-zA-Z\s]*$/.test(value)) {
      setName(value);
      if (value.trim()) {
        setNameError('');
      }
    }
  };

  // FIX: Convert email to lowercase to prevent capital letters
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase(); // <-- lowercase conversion added
    setEmail(value);
    if (validateEmail(value)) {
      setEmailError('');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white w-full max-w-md rounded-lg shadow-lg p-6">
        {/* Modal Header */}
        <div className="flex justify-between items-center border-b pb-3">
          <h2 className="text-lg font-bold">Add Users</h2>
          <button
            type="button"
            onClick={() => {
              // onClose will set isOpen false -> effect will reset
              onClose();
            }}
            className="text-gray-600 hover:text-gray-900"
          >
            ✕
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Name Input */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700">
              Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={handleNameChange}
              placeholder="Name"
              className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm focus:outline-primary"
              required
            />
            {nameError && (
              <p className="text-red-500 text-sm mt-1">{nameError}</p>
            )}
          </div>

          {/* Email Input */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700">
              Email *
            </label>
            <input
              type="text"
              value={email}
              onChange={handleEmailChange}
              placeholder="Enter your email"
              className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm focus:outline-primary"
              required
            />
            {emailError && (
              <p className="text-red-500 text-sm mt-1">{emailError}</p>
            )}
            {duplicateError && (
              <p className="text-red-500 text-sm mt-1">{duplicateError}</p>
            )}
          </div>

          {/* Roles Checkboxes */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700">
              Roles *
            </label>
            <div className="grid grid-cols-2 gap-2 mt-3 border p-2 pt-4 border-gray-300 rounded-md">
              {roles.map((role: Role) => (
                <label
                  key={role.id}
                  className="flex items-center justify-between mr-4 mb-4"
                >
                  <span className="mr-2 text-sm font-semibold">
                    {role.name}
                  </span>
                  <input
                    type="checkbox"
                    className="w-auto h-auto rounded accent-primary"
                    onChange={handleCheckboxChange(Number(role.id))}
                    checked={rolePermissions.includes(Number(role.id))}
                  />
                </label>
              ))}
            </div>
          </div>

          {/* Modal Footer Buttons */}
          <div className="mt-6 flex justify-end gap-4">
            <button
              type="button"
              onClick={() => {
                // onClose will set isOpen false -> effect will reset
                onClose();
              }}
              className="text-gray-600 px-4 py-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                !name.trim() ||
                !email.trim() ||
                !rolePermissions.length ||
                !!emailError ||
                !!nameError
              }
              className={`px-6 py-2 h-10 rounded-md text-white ${name.trim() &&
                  email.trim() &&
                  rolePermissions.length &&
                  !emailError &&
                  !nameError
                  ? 'bg-primary'
                  : 'bg-gray-400'
                }`}
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddUserModal;